import React, { useState, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';

const assessmentItems = [
  {
    title: 'Workflow bottleneck scan',
    desc: 'Where manual work, spreadsheets, inboxes, and handoffs are costing the most time.'
  },
  {
    title: 'Marketplace opportunity review',
    desc: 'Which ecommerce channels and operating gaps are most likely to create practical upside.'
  },
  {
    title: 'Catalog readiness check',
    desc: 'How ready your product data, images, attributes, and SKU structure are for scale.'
  },
  {
    title: 'Automation roadmap',
    desc: 'The first few AI or ecommerce systems worth building, ranked by leverage and feasibility.'
  },
  {
    title: 'Honest fit recommendation',
    desc: 'If we can help, we will tell you where. If we are not the right fit, we will say that too.'
  }
];

const Cta = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [honeypot, setHoneypot] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const formLoadTime = useRef(Date.now());

  const handleSubmit = async e => {
    e.preventDefault();

    // ── Bot Protection ─────────────────────────────────────────────────
    // 1. Honeypot: if hidden field is filled, it's a bot — fake success
    if (honeypot) {
      setSubmitted(true);
      return;
    }
    // 2. Speed check: humans take at least 3 seconds to fill a form
    if (Date.now() - formLoadTime.current < 3000) {
      setSubmitted(true);
      return;
    }
    // ────────────────────────────────────────────────────────────────────

    setSending(true);
    try {
      const db = getFirestore();

      // Write to contactSubmissions — Cloud Function handles email via Postmark
      await addDoc(collection(db, 'contactSubmissions'), {
        ...form,
        createdAt: serverTimestamp(),
        source: 'widestnet-website'
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      alert('Something went wrong. Please email us at sam@widestnet.com');
    }
    setSending(false);
  };

  const handleChange = e =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <section className="wn-cta" id="contact">
      <Container>
        <div className="wn-fade-in">
          <h2 className="wn-section-title centered">
            Let&apos;s find the first place AI or ecommerce can produce
            measurable ROI.
          </h2>
          <p className="wn-section-subtitle centered">
            In 30 minutes, we will look at your operation, identify the
            highest-leverage modernization opportunities, and tell you honestly
            whether we can help.
          </p>
          <ul className="wn-cta-reassurance">
            <li>✓ Principal-led, no junior handoff</li>
            <li>✓ Practical roadmap, not a pitch deck</li>
            <li>✓ Built for legacy distributors and CPG operators</li>
            <li>✓ You own everything we build</li>
          </ul>
        </div>

        <div className="wn-assessment-panel wn-fade-in wn-fade-in-delay-1">
          <div className="wn-assessment-intro">
            <span className="wn-assessment-eyebrow">
              Your assessment includes
            </span>
            <h3>The first 3 places modernization can create ROI.</h3>
            <p>
              We use the call to separate real leverage from noise, then map the
              first practical systems worth building.
            </p>
          </div>
          <div className="wn-assessment-grid">
            {assessmentItems.map((item, i) => (
              <div key={i} className="wn-assessment-item">
                <span>{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {submitted ? (
          <div className="wn-form-success wn-fade-in">
            {/* ── Animated Success Visualization ── */}
            <div className="success-animation">
              {/* Central AI orb */}
              <div className="ai-orb">
                <div className="orb-ring ring-1"></div>
                <div className="orb-ring ring-2"></div>
                <div className="orb-ring ring-3"></div>
                <div className="orb-core">
                  <svg viewBox="0 0 24 24" fill="none" width="32" height="32">
                    <path
                      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                      fill="url(#starGrad)"
                    />
                    <defs>
                      <linearGradient
                        id="starGrad"
                        x1="2"
                        y1="2"
                        x2="22"
                        y2="22"
                      >
                        <stop offset="0%" stopColor="#3ba0d9" />
                        <stop offset="100%" stopColor="#7bc143" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>

              {/* Floating data particles */}
              <div className="particle p1">📊</div>
              <div className="particle p2">🚀</div>
              <div className="particle p3">⚡</div>
              <div className="particle p4">📈</div>
              <div className="particle p5">🤖</div>
              <div className="particle p6">💡</div>

              {/* Growth bars */}
              <div className="growth-bars">
                <div className="bar bar-1"></div>
                <div className="bar bar-2"></div>
                <div className="bar bar-3"></div>
                <div className="bar bar-4"></div>
                <div className="bar bar-5"></div>
              </div>
            </div>

            <h3>You&apos;re all set. We&apos;re on it.</h3>
            <p className="success-subtitle">
              We&apos;ll personally reach out within 1–2 business days to set up
              a call and learn about your business.
            </p>
          </div>
        ) : (
          <form className="wn-cta-form wn-fade-in" onSubmit={handleSubmit}>
            {/* Honeypot — hidden from humans, visible to bots */}
            <div
              style={{
                position: 'absolute',
                left: '-9999px',
                opacity: 0,
                height: 0,
                overflow: 'hidden'
              }}
              aria-hidden="true"
              tabIndex={-1}
            >
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                type="text"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
              />
            </div>

            <div className="form-group">
              <label htmlFor="name">Your Name</label>
              <input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="John Smith"
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Work Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="john@company.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="company">Company Name</label>
              <input
                id="company"
                name="company"
                value={form.company}
                onChange={handleChange}
                required
                placeholder="Your Company"
              />
            </div>
            <div className="form-group">
              <label htmlFor="message">Tell us about your business</label>
              <textarea
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Where are the biggest manual bottlenecks, marketplace gaps, or data problems today?"
              />
            </div>
            <button type="submit" className="wn-btn-primary" disabled={sending}>
              {sending ? 'Sending...' : 'Book Free Assessment →'}
            </button>
          </form>
        )}
      </Container>
    </section>
  );
};

export default Cta;
